import React, {useState, useEffect} from 'react'
import {motion} from 'framer-motion'
import {SupabaseService} from '../../services/supabaseService'
import {ScraperAPIService} from '../../services/scraperAPIService'
import {CreditService} from '../../services/creditService'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const {FiMail, FiLoader, FiEdit, FiPlus, FiSave, FiX} = FiIcons

const EmailExtractor = ({result, user, searchId, onPurchaseClick, onEmailsUpdated}) => {
  const [loading, setLoading] = useState(false)
  const [emails, setEmails] = useState(result.extracted_emails || [])
  const [error, setError] = useState('')
  const [insufficientCredits, setInsufficientCredits] = useState(false)
  const [showManualEntry, setShowManualEntry] = useState(false)
  const [manualEmails, setManualEmails] = useState([''])
  const [savingManual, setSavingManual] = useState(false)
  const [generatedContent, setGeneratedContent] = useState(null)
  const [generatedSubject, setGeneratedSubject] = useState(null)

  // Load emails from database on component mount
  useEffect(() => {
    const loadEmails = async () => {
      try {
        const emailData = await SupabaseService.getExtractedEmails(user.id, searchId)
        const resultEmailData = emailData.find(e => e.result_id === String(result.id))
        if (resultEmailData && resultEmailData.emails) {
          setEmails(resultEmailData.emails)
          if (onEmailsUpdated) onEmailsUpdated(resultEmailData.emails)
        }
      } catch (error) {
        console.error('Error loading emails:', error)
      }
    }
    loadEmails()
  }, [user.id, searchId, result.id])

  // Load generated content for email links
  useEffect(() => {
    const loadGeneratedContent = async () => {
      try {
        const content = await SupabaseService.getGeneratedContent(user.id, searchId)
        const contentForResult = content.find(c => c.result_id === String(result.id))
        if (contentForResult) {
          setGeneratedContent(contentForResult.content || '')
          setGeneratedSubject(contentForResult.subject || '')
        }
      } catch (error) {
        console.error('Error loading generated content:', error)
      }
    }
    loadGeneratedContent()
  }, [user.id, searchId, result.id])

  const extractEmails = async (event) => {
    setLoading(true)
    setError('')
    setInsufficientCredits(false)
    try {
      console.log('Starting email extraction for result:', result.id)
      // First check if user has enough credits (0.0025) and deduct the amount
      const creditUsed = await CreditService.spendFractionalCreditForEmail(user.id, searchId)
      if (!creditUsed) {
        console.log('Credit deduction failed - insufficient credits')
        setInsufficientCredits(true)
        throw new Error('Insufficient credits. You need at least 0.0025 credits to scrape email contacts.')
      }

      console.log('Credit deduction successful, extracting emails...')
      // Try multiple strategies to find the URL
      let url = null

      // Strategy 1: Look for closest parent with flex classes
      let container = event.target.closest('.flex')
      if (container) {
        const linkElement = container.querySelector('a[href]')
        if (linkElement && linkElement.href) {
          url = linkElement.href
        }
      }

      // Strategy 2: If not found, look in the entire result card
      if (!url) {
        container = event.target.closest('[class*="p-4"],[class*="p-6"]')
        if (container) {
          const linkElement = container.querySelector('a[href]')
          if (linkElement && linkElement.href) {
            url = linkElement.href
          }
        }
      }

      // Strategy 3: Use the result URL directly
      if (!url && result.url) {
        url = result.url
      }

      if (!url) {
        setError('Could not find link URL for email extraction')
        setLoading(false)
        return
      }

      console.log('Found URL for extraction:', url)
      // Extract emails using ScraperAPI with the found URL
      const extractedEmails = await ScraperAPIService.extractEmails(url)
      if (extractedEmails.length === 0) {
        setError('No emails found, please visit the page and look for a contact form or any emails that you can add manually')
        setLoading(false)
        return
      }

      // Save emails to database
      await SupabaseService.saveExtractedEmails(
        user.id,
        searchId,
        result.id,
        extractedEmails
      )
      setEmails(extractedEmails)
      if (onEmailsUpdated) onEmailsUpdated(extractedEmails)
    } catch (error) {
      console.error('Error extracting emails:', error)
      if (!insufficientCredits) {
        setError('Failed to scrape email contacts. This action requires 0.0025 credits.')
      } else {
        setInsufficientCredits(true)
        setError('Failed to scrape email contacts. This action requires 0.0025 credits.')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleEmailClick = (email) => {
    let mailtoUrl = `mailto:${email}`

    // Prepare parameters if we have content or subject
    const params = []

    // Add subject if available
    if (generatedSubject && generatedSubject !== 'No subject line detected') {
      params.push(`subject=${encodeURIComponent(generatedSubject)}`)
    }

    // Add body if available
    if (generatedContent) {
      params.push(`body=${encodeURIComponent(generatedContent)}`)
    }

    // Add parameters to mailto URL if we have any
    if (params.length > 0) {
      mailtoUrl += `?${params.join('&')}`
    }

    window.open(mailtoUrl, '_blank')
  }

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const handleManualEmailChange = (index, value) => {
    const updatedEmails = [...manualEmails]
    updatedEmails[index] = value
    setManualEmails(updatedEmails)
  }

  const addEmailField = () => {
    if (manualEmails.length < 3) {
      setManualEmails([...manualEmails, ''])
    }
  }

  const removeEmailField = (index) => {
    if (manualEmails.length > 1) {
      const updatedEmails = manualEmails.filter((_, i) => i !== index)
      setManualEmails(updatedEmails)
    }
  }

  const saveManualEmails = async () => {
    setSavingManual(true)
    setError('')
    try {
      // Filter out empty emails and validate
      const validEmails = manualEmails
        .filter(email => email.trim() !== '')
        .map(email => email.trim())

      // Validate all emails
      const invalidEmails = validEmails.filter(email => !validateEmail(email))
      if (invalidEmails.length > 0) {
        setError('Please enter valid email addresses')
        setSavingManual(false)
        return
      }

      if (validEmails.length === 0) {
        setError('Please enter at least one email address')
        setSavingManual(false)
        return
      }

      // Save emails to database
      await SupabaseService.saveExtractedEmails(
        user.id,
        searchId,
        result.id,
        validEmails
      )
      setEmails(validEmails)
      setShowManualEntry(false)
      setManualEmails([''])
      if (onEmailsUpdated) onEmailsUpdated(validEmails)
    } catch (error) {
      console.error('Error saving manual emails:', error)
      setError('Failed to save emails. Please try again.')
    } finally {
      setSavingManual(false)
    }
  }

  const openManualEntry = () => {
    // Pre-populate with existing emails if any
    if (emails.length > 0) {
      const emailsToEdit = [...emails]
      // Ensure we have at least one empty field for new emails
      if (emailsToEdit.length < 3) {
        emailsToEdit.push('')
      }
      setManualEmails(emailsToEdit)
    } else {
      setManualEmails([''])
    }
    setShowManualEntry(true)
    setError('')
  }

  const cancelManualEntry = () => {
    setShowManualEntry(false)
    setManualEmails([''])
    setError('')
  }

  const closeError = () => {
    setError('')
  }

  // Show manual entry form if requested
  if (showManualEntry) {
    return (
      <div className="space-y-2">
        <div className="text-xs font-medium text-gray-700 mb-2">Add Email Contacts:</div>
        {manualEmails.map((email, index) => (
          <div key={index} className="flex items-center gap-1">
            <input
              type="email"
              value={email}
              onChange={(e) => handleManualEmailChange(index, e.target.value)}
              placeholder="Enter email address"
              className="text-xs px-2 py-1 border border-gray-300 rounded flex-1 focus:ring-1 focus:ring-[#D59A4C] focus:border-transparent"
            />
            {index === manualEmails.length - 1 && manualEmails.length < 3 && (
              <button
                onClick={addEmailField}
                className="p-1 text-green-600 hover:text-green-800"
                title="Add another email"
              >
                <SafeIcon icon={FiPlus} className="w-3 h-3" />
              </button>
            )}
            {manualEmails.length > 1 && (
              <button
                onClick={() => removeEmailField(index)}
                className="p-1 text-red-600 hover:text-red-800"
                title="Remove this email"
              >
                <SafeIcon icon={FiX} className="w-3 h-3" />
              </button>
            )}
          </div>
        ))}
        {error && (
          <div className="text-xs text-red-600 bg-red-50 p-2 rounded">
            {error}
            {insufficientCredits && (
              <span>
                {' '}You must{' '}
                <button
                  onClick={onPurchaseClick}
                  className="text-blue-600 hover:text-blue-800 underline font-medium no-border"
                >
                  buy more credits
                </button>
                .
              </span>
            )}
          </div>
        )}
        <div className="flex gap-2">
          <button
            onClick={saveManualEmails}
            disabled={savingManual}
            className="flex items-center text-xs text-green-600 hover:text-green-800 px-2 py-1 border border-green-300 rounded"
          >
            <SafeIcon
              icon={savingManual ? FiLoader : FiSave}
              className={`w-3 h-3 mr-1 ${savingManual ? 'animate-spin' : ''}`}
            />
            {savingManual ? 'Saving...' : 'Save'}
          </button>
          <button
            onClick={cancelManualEntry}
            className="flex items-center text-xs text-gray-600 hover:text-gray-800 px-2 py-1 border border-gray-300 rounded"
          >
            <SafeIcon icon={FiX} className="w-3 h-3 mr-1" />
            Cancel
          </button>
        </div>
      </div>
    )
  }

  // Show extracted emails if available
  if (emails.length > 0) {
    return (
      <div className="space-y-2">
        <div className="flex items-center">
          <button
            onClick={openManualEntry}
            className="flex items-center text-xs mr-2 no-border"
            style={{border: 'none !important', color: '#d59a4c !important', fontSize: '14px !important'}}
          >
            <SafeIcon icon={FiEdit} className="w-4 h-4" style={{strokeWidth: '3', color: '#d59a4c'}} />
          </button>
          <div className="flex flex-wrap gap-1">
            {emails.map((email, index) => (
              <button
                key={index}
                onClick={() => handleEmailClick(email)}
                className="extracted-email-link text-xs hover:text-blue-800 underline px-3 py-1.5 rounded bg-[#D59A4C30] no-border"
              >
                {email}
              </button>
            ))}
          </div>
        </div>
      </div>
    )
  }

  // Show error if any
  if (error) {
    return (
      <div className="mt-2 text-sm text-red-600 bg-red-50 p-2 rounded">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            {error}
            {insufficientCredits && (
              <span>
                {' '}You must{' '}
                <button
                  onClick={onPurchaseClick}
                  className="text-blue-600 hover:text-blue-800 underline font-medium"
                >
                  buy more credits
                </button>
                .
              </span>
            )}
          </div>
          <button
            onClick={closeError}
            className="ml-2 p-1 text-red-600 hover:text-red-800"
            title="Close message"
          >
            <SafeIcon icon={FiX} className="w-3 h-3" />
          </button>
        </div>
      </div>
    )
  }

  // Show loading state
  if (loading) {
    return (
      <div className="flex items-center text-xs text-gray-600">
        <SafeIcon icon={FiLoader} className="w-3 h-3 mr-1 animate-spin" />
        Extracting emails...
      </div>
    )
  }

  // Show both buttons for Guest Posts, Resource Pages, and now also Niche Collaborations
  if (result.category === 'Guest Posts' || result.category === 'Resource Pages' || result.category === 'Niche Collaborations' || result.category === 'Custom Results') {
    return (
      <div className="flex items-center gap-2">
        <button
          onClick={openManualEntry}
          className="flex items-center text-xs text-[#D59A4C] hover:text-[#B58C54] transition-colors px-1 py-1"
          style={{border: 'none !important', color: '#d59a4c !important', fontSize: '14px !important'}}
        >
          <SafeIcon icon={FiEdit} className="w-4 h-4 mr-1" style={{strokeWidth: '3', color: '#d59a4c'}} />
          <span style={{fontWeight: 'bold'}}>Add Emails</span>
        </button>
        <button
          onClick={extractEmails}
          className="flex items-center text-xs text-blue-600 hover:text-blue-800 transition-colors px-3 py-1.5 rounded bg-[#D59A4C30] no-border"
          style={{border: 'none !important', color: '#d59a4c !important', fontSize: '14px !important'}}
        >
          <SafeIcon icon={FiMail} className="w-4 h-4 mr-1" style={{strokeWidth: '3', color: '#d59a4c'}} />
          <span style={{fontWeight: 'bold'}}>Get Emails</span>
        </button>
      </div>
    )
  }

  // Return null for other categories
  return null
}

export default EmailExtractor